import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Confetti from 'react-confetti';
import { Button, Card, Space, Typography, message, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import FileUpload from '../../../components/FileUpload';
import CustomModal from '../../../components/CustomModal';
import { api, longApi } from '../../../utils/apiClient';
import { useUser } from '../../../contexts/UserContext';
import { PageHeader, TGDataTable } from '../../../components/common';

const { Text } = Typography;

// Render steps with proper formatting
const renderSteps = (steps) => {
  if (!steps) return '';
  const formattedSteps = Array.isArray(steps)
    ? steps.map(step => step.Instruction || step).join('\n')
    : steps;
  return formattedSteps;
};

const TestCaseGenerator = () => {
  const { user } = useUser();

  // Initialize state from sessionStorage to persist across navigation
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentContent, setDocumentContent] = useState(() => {
    const saved = sessionStorage.getItem('testCaseGeneratorData');
    return saved ? JSON.parse(saved) : '';
  });
  const [uploadedFileName, setUploadedFileName] = useState(() => {
    return sessionStorage.getItem('testCaseGeneratorFileName') || '';
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeButton, setActiveButton] = useState(() => {
    return sessionStorage.getItem('testCaseGeneratorActiveButton') || '';
  });

  // Dynamic settings state
  const [settings, setSettings] = useState({});
  const [outputFormats, setOutputFormats] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const { register, watch } = useForm({
    defaultValues: {
      model: 'ModelA' // Set default model to Model A
    }
  });
  const selectedModel = watch('model');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadedFileName(file.name);
      const formData = new FormData();
      formData.append('file', file);

      longApi.post('/file_upload', formData)
        .then(() => {
          setUploadedFileName(file.name);
          setShowModal(false);
        })
        .catch(error => {
          console.error('Error during file upload:', error);
          console.error('Error response:', error.response?.data);
          console.error('Error status:', error.response?.status);
          const errorMsg = error.response?.data?.error || 'Error uploading file. Please try again.';
          setUploadedFileName('Click here to upload file');
          setModalMessage(errorMsg);
          setShowModal(true);
        });
    } else {
      setModalMessage('Please select a file to upload.');
      setShowModal(true);
    }
  };

  const handleAction = (action, modelType) => {
    if (!uploadedFileName || uploadedFileName === settings.upload_placeholder) {
      setModalMessage('Please upload a file first.');
      setShowModal(true);
      return;
    }
    const actionStartTime = new Date().toISOString();
    setIsLoading(true);

    // Use longApi for generate-cases (OpenAI takes >60s, bypasses CloudFront timeout)
    // Use regular api for other actions
    const apiClient = action === 'generate-cases' ? longApi : api;

    apiClient.post(`/${action}`, {
      model_type: modelType,
      action_start: actionStartTime,
    })
    .then(response => {
      setDocumentContent(response.data.result);
      const actionEndTime = new Date().toISOString();
      const userActivityId = response.data.user_activity_id;
      if (userActivityId) {
        api.post('/audit_action_end', {
          user_activity_id: userActivityId,
          action_end: actionEndTime,
        });
      } else {
        console.error('No user activity ID returned from the action initiation call.');
      }
    })
    .catch(error => {
      console.error(`Error during ${action} request:`, error);
      setModalMessage('Failed to process the request. Please try again.');
      setShowModal(true);
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Function to export data to .docx
  const exportToDocx = async () => {
    try {
      // Extract headers from columns
      const columns = getColumns();
      const tableData = parseDocumentContentToTable(documentContent);
      const headers = columns.map(col => col.title);

      const response = await api.post(
        '/export_to_word',
        { data: tableData, headers: headers },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'test_cases.docx'; // The default file name for download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('There was an error exporting the document:', error);
    }
  };

  // Dynamic column definitions for TGDataTable based on selected output format
  const getColumns = () => {
    if (selectedFormat && selectedFormat.column_headers && Array.isArray(selectedFormat.column_headers)) {
      // Use custom column headers from selected format
      return selectedFormat.column_headers.map((header) => {
        const col = {
          key: header,
          title: header,
          dataIndex: header,
          width: header === 'Test Case ID' ? 120 : 200,
          ellipsis: true,
          sortable: true,
          filterable: true,
          filterType: 'text',
        };

        // Special handling for Steps column
        if (header === 'Steps') {
          col.render = (steps) => (
            <div style={{ whiteSpace: 'pre-wrap' }}>{renderSteps(steps)}</div>
          );
        }

        return col;
      });
    }

    // Default columns (pharma standard)
    return [
      {
        key: 'Test Case ID',
        title: 'Test Case ID',
        dataIndex: 'Test Case ID',
        width: 120,
        fixed: 'left',
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        key: 'Test Case Name',
        title: 'Test Case Name',
        dataIndex: 'Test Case Name',
        width: 200,
        ellipsis: true,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        key: 'Objective',
        title: 'Objective',
        dataIndex: 'Objective',
        width: 250,
        ellipsis: true,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        key: 'Steps',
        title: 'Steps',
        dataIndex: 'Steps',
        width: 300,
        render: (steps) => (
          <div style={{ whiteSpace: 'pre-wrap' }}>{renderSteps(steps)}</div>
        ),
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        key: 'Expected Result',
        title: 'Expected Result',
        dataIndex: 'Expected Result',
        width: 250,
        ellipsis: true,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        key: 'Actual Result',
        title: 'Actual Result',
        dataIndex: 'Actual Result',
        width: 200,
        ellipsis: true,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        key: 'Comments',
        title: 'Comments',
        dataIndex: 'Comments',
        width: 200,
        ellipsis: true,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
    ];
  };

  const columns = getColumns();

  const parseDocumentContentToTable = (documentContent) => {
    if (Array.isArray(documentContent)) {
      return documentContent.map((testCase, index) => ({
        ...testCase,
        // Add unique key for table
        key: testCase['Test Case ID'] || `test-case-${index}`,
      }));
    }
    console.error('Document content is not an array:', documentContent);
    return [];
  };

  const tableData = parseDocumentContentToTable(documentContent);

  // Clear stale session data on mount if user session changed
  // This handles hard refresh, expired tokens, or logout+login scenarios
  useEffect(() => {
    const currentUserId = user?.id;
    const storedUserId = sessionStorage.getItem('testCaseGeneratorUserId');

    // If user changed or session is stale, clear all test case generator data
    if (storedUserId && storedUserId !== String(currentUserId)) {
      console.log('[TestCaseGenerator] User session changed, clearing stale upload data');
      sessionStorage.removeItem('testCaseGeneratorData');
      sessionStorage.removeItem('testCaseGeneratorFileName');
      sessionStorage.removeItem('testCaseGeneratorActiveButton');
      sessionStorage.removeItem('testCaseGeneratorUserId');

      // Reset component state
      setUploadedFileName('');
      setDocumentContent('');
      setActiveButton('');
      setSelectedFile(null);
    }

    // Store current user ID for future validation
    if (currentUserId) {
      sessionStorage.setItem('testCaseGeneratorUserId', String(currentUserId));
    }
  }, [user]);

  useEffect(() => {
    if (documentContent) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    }
  }, [documentContent]);

  // Persist data to sessionStorage
  useEffect(() => {
    if (documentContent) {
      sessionStorage.setItem('testCaseGeneratorData', JSON.stringify(documentContent));
    }
  }, [documentContent]);

  useEffect(() => {
    if (uploadedFileName) {
      sessionStorage.setItem('testCaseGeneratorFileName', uploadedFileName);
    }
  }, [uploadedFileName]);

  useEffect(() => {
    if (activeButton) {
      sessionStorage.setItem('testCaseGeneratorActiveButton', activeButton);
    }
  }, [activeButton]);

  // Load settings and output formats on mount
  useEffect(() => {
    const loadSettingsAndFormats = async () => {
      try {
        setSettingsLoading(true);

        // Load industry presets (publicly accessible, no auth required)
        const presetsResponse = await api.get('/aiml/industry-presets');
        const pharmaDefaults = presetsResponse.data.pharma || {};

        // Try to load org-specific settings (requires auth and admin privilege)
        // If this fails (403/401), fall back to pharma defaults
        let orgSettings = {};
        try {
          const settingsResponse = await api.get('/aiml/settings');
          orgSettings = settingsResponse.data;
        } catch (settingsError) {
          console.log('Using default pharma settings (org settings not accessible)');
        }

        // Merge org settings with pharma defaults (org settings take precedence)
        const mergedSettings = {
          button_label_objectives: orgSettings.button_label_objectives?.value || pharmaDefaults.button_label_objectives || 'Test Objectives',
          button_label_generate: orgSettings.button_label_generate?.value || pharmaDefaults.button_label_generate || 'Generate Cases',
          upload_placeholder: orgSettings.upload_placeholder?.value || pharmaDefaults.upload_placeholder || 'Upload Specifications/Requirements',
          test_objectives_prompt: orgSettings.test_objectives_prompt?.value || pharmaDefaults.test_objectives_prompt,
          generate_cases_prompt: orgSettings.generate_cases_prompt?.value || pharmaDefaults.generate_cases_prompt,
        };
        setSettings(mergedSettings);

        // Load output formats (requires auth)
        try {
          const formatsResponse = await api.get('/aiml/output-formats');
          setOutputFormats(formatsResponse.data);

          // Find and set default format
          const defaultFormat = formatsResponse.data.find(f => f.is_default);
          if (defaultFormat) {
            setSelectedFormat(defaultFormat);
          }
        } catch (formatsError) {
          console.log('Output formats not accessible, using default table structure');
        }

      } catch (error) {
        console.error('Error loading settings:', error);
        // Set pharma defaults if everything fails
        setSettings({
          button_label_objectives: 'Test Objectives',
          button_label_generate: 'Generate Cases',
          upload_placeholder: 'Upload Specifications/Requirements',
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettingsAndFormats();
  }, []);

  const handleButtonClick = (actionType) => {
    if (!user) {
      console.error('No user logged in');
      return;
    }
    handleAction(actionType, selectedModel);
    setActiveButton(actionType); // Set the active button
  };

  return (
    <div>
      {showConfetti && <Confetti />}

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%', alignItems: 'center' }}>
          <FileUpload
            onFileSelect={handleFileSelect}
            uploadedFileName={uploadedFileName || settings.upload_placeholder || "Upload Specifications/Requirements"}
          />

          <Space size="middle">
            <Button
              type={activeButton === 'test-objectives' ? 'primary' : 'default'}
              onClick={() => handleButtonClick('test-objectives')}
              disabled={settingsLoading}
              size="large"
            >
              {settings.button_label_objectives || 'Test Objectives'}
            </Button>
            <Button
              type={activeButton === 'generate-cases' ? 'primary' : 'default'}
              onClick={() => handleButtonClick('generate-cases')}
              disabled={settingsLoading}
              size="large"
            >
              {settings.button_label_generate || 'Generate Cases'}
            </Button>
          </Space>

          {isLoading && (
            <div style={{ textAlign: 'center', padding: '20px 0', width: '100%' }}>
              <Spin size="large" />
              <div style={{ marginTop: '12px' }}>
                <Text type="secondary">Gearing up for action...</Text>
              </div>
            </div>
          )}
        </Space>
      </Card>

      {!isLoading && user && documentContent && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', marginBottom: '16px' }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportToDocx}
            size="large"
          >
            Export to Word
          </Button>
        </div>
      )}

      {/* Conditional rendering based on output format type */}
      {documentContent && (
        selectedFormat && selectedFormat.format_type === 'freeform' ? (
          // Freeform text output
          <Card style={{ marginTop: '16px' }}>
            <div style={{
              minHeight: '200px',
              whiteSpace: 'pre-wrap'
            }}>
              {typeof documentContent === 'string' ? (
                <div>{documentContent}</div>
              ) : (
                <div>{JSON.stringify(documentContent, null, 2)}</div>
              )}
            </div>
          </Card>
        ) : (
          // Table output (default) - using TGDataTable from custom framework
          <div style={{ marginTop: '16px' }}>
            <TGDataTable
              data={tableData}
              columns={columns}
              rowKey="key"
              pagination="client"
              pageSize={10}
              showExport
              showColumnConfig
              exportFilename="test_cases"
              presetKey="aiml.test_cases"
            />
          </div>
        )
      )}

      {showModal && (
        <CustomModal
          message={modalMessage}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default TestCaseGenerator;
